import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { UserProfileApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

export const useFamilyContactsDelete = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isSuccess, isError, isPending } = useMutation({
    mutationFn: async ({
      userId,
      userContactId,
    }: {
      userId: number;
      userContactId: number;
    }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(UserProfileApi, accessToken);
      return service.apiUserProfileIdContactsUserContactIdDelete({
        id: userId,
        userContactId,
      });
    },
    onSuccess: async (_data, variables) => {
      toast({
        title: "Family contact deleted",
        description: "Family contact deleted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });

      queryClient.invalidateQueries({
        queryKey: [QueryKeys.FamilyContactsGet, variables.userId, "all"],
      });
    },
    onError: async (error: any) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to delete family contact";
      
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isSuccess, isError, isPending };
};
